import { useEffect, useMemo, useRef, useState } from "react";
import type { FormEvent } from "react";
import { addFavorite, listCategories, listListings, listMyFavoriteListingIds, removeFavorite } from "../lib/api";
import type { Category, Listing } from "../types/domain";
import { ListingCard } from "../components/ListingCard";
import { LocationPicker } from "../components/LocationPicker";
import { Icon, categoryIcon } from "../components/Icon";
import { CategoryCascadeSelect } from "../components/CategoryCascadeSelect";
import { CategoryTreePicker } from "../components/CategoryTreePicker";
import { StandardSelect } from "../components/StandardSelect";
import { demoCategories, demoListings } from "../data/demoData";
import { useAuth } from "../lib/auth";
import { Notice } from "../components/Notice";
import { readSearchSession, writeSearchSession } from "../lib/searchSession";
import { categoryPath, isConditionApplicableForCategories } from "../lib/categories";

const HOME_ROOT_CATEGORY_SLUGS = new Set(demoCategories.map((category) => category.slug));
const CONDITION_OPTIONS = [{ value: "", label: "Любое" }, { value: "new", label: "Новое" }, { value: "like_new", label: "Как новое" }, { value: "used", label: "Б/у" }];
const SORT_OPTIONS = [{ value: "newest", label: "Сначала новые" }, { value: "price_asc", label: "Дешевле" }, { value: "price_desc", label: "Дороже" }];
const DEFAULT_TAG_FILTERS = ["веганское", "без кожи", "эко-кожа", "ручная работа", "вегетарианское", "без яиц"];

type CollectionFilter = "recommended" | "newest" | "nearby" | "popular" | "vegan" | "no-leather" | "used" | "tag";

function normalizeTag(value: string): string {
  return value.trim().toLowerCase();
}

function hasTag(listing: Listing, tags: string[]) {
  const normalizedTags = (listing.ethical_tags ?? []).map(normalizeTag);
  return tags.some((tag) => normalizedTags.includes(normalizeTag(tag)));
}

function recommendedScore(listing: Listing): number {
  const photosScore = listing.photos?.length ? 1000 : 0;
  const sellerScore = listing.profile?.is_blocked ? -5000 : 0;
  const popularityScore = (listing.favorites_count ?? 0) * 8 + (listing.views_count ?? 0);
  const ageScore = new Date(listing.created_at).getTime() / 100000000;
  return photosScore + sellerScore + popularityScore + ageScore;
}

function sortListingsForCollection(items: Listing[], collection: CollectionFilter): Listing[] {
  const next = [...items];
  if (collection === "popular") {
    return next.sort((a, b) => (
      (b.favorites_count ?? 0) * 8 + (b.views_count ?? 0)
    ) - (
      (a.favorites_count ?? 0) * 8 + (a.views_count ?? 0)
    ) || new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }
  if (collection === "recommended") {
    return next.sort((a, b) => recommendedScore(b) - recommendedScore(a));
  }
  return next;
}

export function HomePage({ go }: { go: (route: string, params?: Record<string, string>) => void }) {
  const { user } = useAuth();
  const [initialSearch] = useState(() => readSearchSession());
  const [categories, setCategories] = useState<Category[]>([]);
  const [listings, setListings] = useState<Listing[]>([]);
  const [favoriteIds, setFavoriteIds] = useState<Set<number>>(() => new Set());
  const [favoriteBusyIds, setFavoriteBusyIds] = useState<Set<number>>(() => new Set());
  const [favoriteMessage, setFavoriteMessage] = useState<string | null>(null);
  const [query, setQuery] = useState(initialSearch?.query ?? "");
  const [categoryId, setCategoryId] = useState<number | null>(initialSearch?.categoryId ?? null);
  const [countryCode, setCountryCode] = useState<string | null>(initialSearch ? initialSearch.countryCode ?? null : "RU");
  const [cities, setCities] = useState<string[]>(initialSearch?.cities ?? []);
  const [regions, setRegions] = useState<string[]>(initialSearch?.regions ?? []);
  const [condition, setCondition] = useState("");
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [sort, setSort] = useState<"newest" | "price_asc" | "price_desc">("newest");
  const [collectionFilter, setCollectionFilter] = useState<CollectionFilter>("recommended");
  const [selectedTagFilter, setSelectedTagFilter] = useState("");
  const [tagMenuOpen, setTagMenuOpen] = useState(false);
  const [collectionMessage, setCollectionMessage] = useState<string | null>(null);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [listingsReady, setListingsReady] = useState(false);
  const tagMenuRef = useRef<HTMLDivElement | null>(null);
  const listingIdsKey = listings.map((listing) => listing.id).join("|");

  useEffect(() => {
    void listCategories()
      .then(setCategories)
      .catch((error) => console.warn(error));
  }, []);

  useEffect(() => {
    writeSearchSession({ query, categoryId, countryCode, cities, regions });
  }, [query, categoryId, countryCode, cities.join("|"), regions.join("|")]);

  useEffect(() => {
    if (!tagMenuOpen) return;
    const close = (event: MouseEvent) => {
      if (!tagMenuRef.current?.contains(event.target as Node)) setTagMenuOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [tagMenuOpen]);

  useEffect(() => {
    let alive = true;
    setListingsReady(false);
    void listListings({
      query,
      categoryId,
      countryCode,
      cities,
      regions,
      condition: condition || null,
      priceMin: priceMin ? Number(priceMin) : null,
      priceMax: priceMax ? Number(priceMax) : null,
      sort
    })
      .then((nextListings) => {
        if (!alive) return;
        setListings(nextListings);
        setListingsReady(true);
      })
      .catch((error) => {
        if (!alive) return;
        console.warn(error);
        setListingsReady(true);
      });
    return () => {
      alive = false;
    };
  }, [query, categoryId, countryCode, cities.join("|"), regions.join("|"), condition, priceMin, priceMax, sort]);

  useEffect(() => {
    if (!user || !listings.length) {
      setFavoriteIds(new Set());
      return;
    }
    let alive = true;
    void listMyFavoriteListingIds(listings.map((listing) => listing.id))
      .then((ids) => {
        if (alive) setFavoriteIds(new Set(ids));
      })
      .catch((error) => console.warn(error));
    return () => {
      alive = false;
    };
  }, [user?.id, listingIdsKey]);

  const toggleFavorite = async (listing: Listing) => {
    if (!user) {
      go("login");
      return;
    }
    if (listing.user_id === user.id || favoriteBusyIds.has(listing.id)) return;
    setFavoriteMessage(null);
    const wasFavorite = favoriteIds.has(listing.id);
    setFavoriteBusyIds((current) => new Set(current).add(listing.id));
    setFavoriteIds((current) => {
      const next = new Set(current);
      if (wasFavorite) next.delete(listing.id);
      else next.add(listing.id);
      return next;
    });
    try {
      if (wasFavorite) await removeFavorite(listing.id);
      else await addFavorite(listing.id);
    } catch (error) {
      console.warn(error);
      setFavoriteIds((current) => {
        const next = new Set(current);
        if (wasFavorite) next.add(listing.id);
        else next.delete(listing.id);
        return next;
      });
      setFavoriteMessage("Не удалось обновить избранное.");
    } finally {
      setFavoriteBusyIds((current) => {
        const next = new Set(current);
        next.delete(listing.id);
        return next;
      });
    }
  };

  const availableTagFilters = useMemo(() => {
    const tags = new Set(DEFAULT_TAG_FILTERS);
    listings.forEach((listing) => listing.ethical_tags?.forEach((tag) => {
      const normalized = tag.trim();
      if (normalized) tags.add(normalized);
    }));
    return Array.from(tags).sort((a, b) => a.localeCompare(b, "ru"));
  }, [listings]);
  const visibleCategories = categories.length ? categories : demoCategories;
  const sourceListings = listingsReady ? listings : demoListings;
  const visibleListings = useMemo(() => {
    let next = sourceListings;
    if (collectionFilter === "vegan") {
      next = next.filter((listing) => hasTag(listing, ["веганское", "веганский", "vegan"]));
    } else if (collectionFilter === "no-leather") {
      next = next.filter((listing) => (
        listing.contains_leather === "no"
        || hasTag(listing, ["без кожи", "эко-кожа", "экокожа", "искусственная кожа"])
      ) && listing.contains_fur !== "yes");
    } else if (collectionFilter === "used") {
      next = next.filter((listing) => listing.item_condition === "used");
    } else if (collectionFilter === "tag" && selectedTagFilter) {
      next = next.filter((listing) => hasTag(listing, [selectedTagFilter]));
    }
    return sortListingsForCollection(next, collectionFilter);
  }, [collectionFilter, selectedTagFilter, sourceListings]);
  const topCategories = visibleCategories
    .filter((category) => HOME_ROOT_CATEGORY_SLUGS.has(category.slug) && (category.parent_id == null || category.parent_id === 0))
    .sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name, "ru"))
    .slice(0, 12);
  const topCategoryIds = new Set(topCategories.map((category) => category.id));
  const categoryById = new Map(visibleCategories.map((category) => [category.id, category]));
  const isInHomeTree = (category: Category): boolean => {
    let cursor: Category | undefined = category;
    while (cursor) {
      if (topCategoryIds.has(cursor.id)) return true;
      cursor = cursor.parent_id ? categoryById.get(cursor.parent_id) : undefined;
    }
    return false;
  };
  const homeTreeCategories = visibleCategories.filter(isInHomeTree);
  const conditionApplicable = isConditionApplicableForCategories(categoryPath(categories, categoryId));

  useEffect(() => {
    if (!conditionApplicable && condition) setCondition("");
  }, [conditionApplicable, condition]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const params: Record<string, string> = {};
    if (query.trim()) params.q = query.trim();
    if (categoryId) params.category = String(categoryId);
    if (countryCode) params.country = countryCode;
    if (cities.length) params.cities = cities.join(",");
    if (regions.length) params.regions = regions.join(",");
    writeSearchSession({ query: query.trim(), categoryId, countryCode, cities, regions });
    go("catalog", params);
  };
  const applyCollection = (nextFilter: CollectionFilter) => {
    setCollectionMessage(null);
    setCollectionFilter(nextFilter);
    if (nextFilter !== "tag") setSelectedTagFilter("");
    if (nextFilter === "newest" || nextFilter === "recommended") {
      setSort("newest");
      setCondition("");
    }
    if (nextFilter === "used") setCondition("used");
  };
  const applyNearbyCollection = async () => {
    setCollectionMessage(null);
    if (!navigator.geolocation) {
      setCollectionMessage("Браузер не поддерживает геопозицию.");
      return;
    }
    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 });
      });
      const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${position.coords.latitude}&lon=${position.coords.longitude}&accept-language=ru`);
      if (!response.ok) throw new Error("reverse_geocode_failed");
      const payload = await response.json() as { address?: Record<string, string> };
      const address = payload.address ?? {};
      const city = address.city || address.town || address.village || address.municipality || address.county;
      if (!city) {
        setCollectionMessage("Не удалось определить город по геопозиции.");
        return;
      }
      setCountryCode(address.country_code ? address.country_code.toUpperCase() : "RU");
      setCities([city]);
      setRegions([]);
      setCollectionFilter("nearby");
      setSelectedTagFilter("");
    } catch (error) {
      console.warn(error);
      setCollectionMessage("Не удалось получить геопозицию. Проверьте разрешение в браузере.");
    }
  };

  return (
    <>
      <section className="search-strip">
        <button className="category-button home-desktop-category-button" type="button" onClick={() => go("catalog")}><Icon name="grid" />Все категории</button>
        <form className="search-form" onSubmit={submit}>
          <div className="catalog-search-field">
            <input placeholder="Поиск по объявлениям" value={query} onChange={(event) => setQuery(event.target.value)} />
            <button
              className={`catalog-search-filter-button${mobileFiltersOpen ? " active" : ""}`}
              type="button"
              onClick={(event) => {
                event.preventDefault();
                setMobileFiltersOpen((open) => !open);
              }}
              aria-label="Фильтры"
              aria-expanded={mobileFiltersOpen}
            >
              <Icon name="sliders" />
            </button>
          </div>
          <CategoryCascadeSelect categories={homeTreeCategories} value={categoryId} onChange={setCategoryId} />
          <LocationPicker allowAllCountries allowRegions countryCode={countryCode} cities={cities} regions={regions} className="search-location" onChange={(next) => { setCountryCode(next.countryCode); setCities(next.cities); setRegions(next.regions ?? []); }} />
          <div className={`filters catalog-mobile-filters home-mobile-filters${mobileFiltersOpen ? " mobile-open" : ""}`}>
            <div className="price-filter-group">
              <span className="filter-label">Цена</span>
              <div className="two price-filter">
                <label><span className="price-filter-label">Цена от</span><input type="number" value={priceMin} placeholder="От" onChange={(event) => setPriceMin(event.target.value)} /></label>
                <label><span className="price-filter-label">до</span><input type="number" value={priceMax} placeholder="До" onChange={(event) => setPriceMax(event.target.value)} /></label>
              </div>
            </div>
            {conditionApplicable ? <label><span className="filter-label">Состояние</span><StandardSelect value={condition} options={CONDITION_OPTIONS} onChange={setCondition} /></label> : null}
            <div className="catalog-mobile-sort">
              <span className="filter-label">Сортировка</span>
              <StandardSelect className="catalog-sort-select catalog-mobile-sort-select" value={sort} options={SORT_OPTIONS} onChange={(value) => setSort(value as "newest" | "price_asc" | "price_desc")} />
            </div>
          </div>
          <button type="submit">Найти</button>
        </form>
      </section>
      <div className="home-layout">
        <aside className="sidebar home-sidebar home-category-tree">
          <CategoryTreePicker
            categories={homeTreeCategories}
            value={null}
            onChange={(nextCategoryId) => go("catalog", { category: String(nextCategoryId) })}
            compact
            getIcon={(category, columnIndex) => columnIndex === 0 ? <Icon name={categoryIcon(category.slug)} /> : null}
          />
        </aside>
        <section className="home-main">
          <section className="hero">
            <h1>Этичный маркетплейс объявлений</h1>
            <p>Покупайте и продавайте с заботой о себе, людях, животных и планете.</p>
            <div className="hero-points">
              <span><Icon name="ethical-shield" /><b>Безопасно</b><small>Проверяем объявления<br />и защищаем вас</small></span>
              <span><Icon name="ethical-leaf" /><b>Этично</b><small>Товары и услуги,<br />соответствующие ценностям</small></span>
              <span><Icon name="ethical-users" /><b>С заботой</b><small>Поддерживаем добрые<br />сообщества и проекты</small></span>
              <span><Icon name="ethical-eco" /><b>Экологично</b><small>Меньше вреда природе,<br />больше осознанности</small></span>
            </div>
            <div className="hero-standards">
              <span><Icon name="ethical-shield" />Без кожи</span>
              <span><Icon name="no-egg" />Без яиц</span>
              <span><Icon name="no-bug" />Без кармина</span>
              <span><Icon name="sprout-simple" />Вегетарианское и Веганское</span>
            </div>
          </section>
          <div className="section-title-row">
            <h2>Подборки для вас</h2>
            <a href="/catalog" onClick={(event) => { event.preventDefault(); go("catalog"); }}>Смотреть все ›</a>
          </div>
          <div className="chips">
            <button className={`chip${collectionFilter === "recommended" ? " active" : ""}`} type="button" onClick={() => applyCollection("recommended")}>Рекомендуем</button>
            <button className={`chip${collectionFilter === "newest" ? " active" : ""}`} type="button" onClick={() => applyCollection("newest")}>Новинки</button>
            <button className={`chip${collectionFilter === "nearby" ? " active" : ""}`} type="button" onClick={() => void applyNearbyCollection()}>Рядом с вами</button>
            <button className={`chip${collectionFilter === "popular" ? " active" : ""}`} type="button" onClick={() => applyCollection("popular")}>Популярное</button>
            <button className={`chip${collectionFilter === "vegan" ? " active" : ""}`} type="button" onClick={() => applyCollection("vegan")}>Веганское</button>
            <button className={`chip${collectionFilter === "no-leather" ? " active" : ""}`} type="button" onClick={() => applyCollection("no-leather")}>Без кожи</button>
            <div className="chip-dropdown" ref={tagMenuRef}>
              <button className={`chip chip-dropdown-toggle${collectionFilter === "tag" ? " active" : ""}`} type="button" onClick={() => setTagMenuOpen((open) => !open)} aria-expanded={tagMenuOpen}>
                {selectedTagFilter || "Теги"} <span>⌄</span>
              </button>
              {tagMenuOpen ? (
                <div className="chip-dropdown-menu">
                  {availableTagFilters.map((tag) => (
                    <button
                      className={selectedTagFilter === tag ? "active" : ""}
                      type="button"
                      onClick={() => {
                        setSelectedTagFilter(tag);
                        setCollectionFilter("tag");
                        setTagMenuOpen(false);
                      }}
                      key={tag}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
            <button className={`chip${collectionFilter === "used" ? " active" : ""}`} type="button" onClick={() => applyCollection("used")}>Б/у</button>
          </div>
          {collectionMessage ? <Notice kind="error">{collectionMessage}</Notice> : null}
          {favoriteMessage ? <Notice kind="error">{favoriteMessage}</Notice> : null}
          <div className="grid">
            {visibleListings.map((listing) => (
              <ListingCard
                listing={listing}
                go={go}
                showFavorite={!user || listing.user_id !== user.id}
                isFavorite={favoriteIds.has(listing.id)}
                favoriteBusy={favoriteBusyIds.has(listing.id)}
                onFavoriteToggle={toggleFavorite}
                key={listing.id}
              />
            ))}
          </div>
        </section>
      </div>
    </>
  );
}
